/*
  Sacrasoul - Klaviyo onsite identity (SSKlv) + browse tracking.

  Extracted from snippets/klaviyo-onsite.liquid on 2026-09-11. It was 6,558 B
  (7,294 B on a product page) of executable inline script in the <head> of every
  document. Nothing in it needed to be there: SSKlv only has to exist before a
  BODY script calls it, and the snippet now defines a 300-byte queueing stub for
  exactly that window. Everything Liquid can see - the logged-in customer's
  email and the Viewed Product payload - is handed over as window.__ssKlv.

  No consent gate since 2026-10-06 (Sho; decision record
  sacrasoul-brain/strategy/decisions/2026-10-06-klaviyo-and-google-consent-default-granted.md):
  identify and Viewed Product go out whatever the visitor answers the cookie
  banner, so abandoned-basket and browse emails have the data they need. Until
  then everything we pushed waited for marketing consent (a three-state
  pending/granted/declined buffer); that version is in git history. Email
  consent is separate and unchanged: Klaviyo still emails only subscribers.
  The one gate left is internalVisit() below: our own visits never load
  klaviyo.js and push nothing. The klaviyo.js URL contract and the
  interaction-or-idle load are unchanged.
*/
(function () {

/*
  The queue is Klaviyo's own contract, kept verbatim from the embed we replaced:
  _klOnsite holds calls made before klaviyo.js exists and klaviyo.js drains it on
  load; once it has loaded, window.klaviyo is the real object and takes the push
  directly. We never create window.klaviyo ourselves, so its presence is the
  honest answer to "has the SDK landed".
*/
  function pushKlaviyo(args) {
    if (window.klaviyo && typeof window.klaviyo.push === 'function') window.klaviyo.push(args);
    else (window._klOnsite = window._klOnsite || []).push(args);
  }

/*
  Our own browsing is not a shopper. A Klaviyo audit on 2026-10-04 found 37
  Viewed Product events from theme previews in one day and 4 internal profiles,
  because nothing here asked. When the visit is ours, klaviyo.js is never
  fetched (it alone sends Active on Site) and nothing is pushed. Since
  2026-10-06 this is the only gate on what we push (no consent gate).

  The signals are the ones assets/ss-attribution.js internalVisit() and
  assets/ss-posthog.js internalReason() read: the ss_internal marker in the URL,
  in sessionStorage/localStorage or as the ss_internal=1 cookie (the one marker
  since 2026-10-05), the theme editor, a theme preview, the Paseo shell, and a
  browser under automation. A copy rather than a shared helper: this file runs
  from the <head> (snippets/klaviyo-onsite.liquid) before ss-attribution.js
  exists, and the head order is load-bearing. A preview is read from
  Shopify.theme.role and preview_theme_id, which exist when this deferred file
  runs, not from the preview bar, which Shopify injects later (it is still
  checked, for the calls that come after it lands). ?ss_internal=0 turns off
  only the remembered copies, never the editor or a preview.
*/
  function internalVisit() {
    var flag = null;
    try { flag = new URL(window.location.href).searchParams.get('ss_internal'); } catch (e) {}
    if (flag === '1') return true;
    if (flag !== '0') {
      try { if (/(?:^|;)\s*ss_internal=1(?:;|$)/.test(document.cookie || '')) return true; } catch (e) {}
      try { if (window.sessionStorage.getItem('ss_internal') === '1') return true; } catch (e) {}
      try { if (window.localStorage.getItem('ss_internal') === '1') return true; } catch (e) {}
    }
    try {
      var shop = window.Shopify || {};
      if (shop.designMode) return true;
      if (shop.theme && shop.theme.role && shop.theme.role !== 'main') return true;
      if (/[?&]preview_theme_id=/.test(window.location.search || '')) return true;
      if (document.getElementById && document.getElementById('preview-bar-iframe')) return true;
    } catch (e) {}
    try {
      if ((window.navigator.userAgent || '').indexOf('Paseo/') !== -1) return true;
      if (window.navigator.webdriver === true) return true;
    } catch (e) {}
    return false;
  }

  function submit(args) {
    if (internalVisit()) return;
    pushKlaviyo(args);
  }

/*
  Loading klaviyo.js ourselves. The URL is not free-form: the shipped klaviyo.js
  reads document.currentScript and refuses to run unless the src matches
  /onsite/js/<company>/klaviyo.js?company_id=<company>, logging
  "Not loading ... for VFiMuh" and returning. Verified against the live file,
  2026-09-11. The company id is public (it is in the URL of every page that
  loads the SDK today) and changes only if the Klaviyo account does.

  Prose lives in Liquid comments, not JS comments: this script is inlined into
  every HTML document, so a paragraph here is bytes on every page view.
*/
  var KLAVIYO_COMPANY_ID = 'VFiMuh';
  var klaviyoRequested = false;
  /* 'wheel', not 'scroll': the browser fires a trusted scroll by itself during
     load (restored position, anchors, reflow), which pulled klaviyo.js into the
     first-paint window. Touch scrolls still wake it through touchstart /
     pointerdown. Same fix as snippets/gtm-loader.liquid (2026-09-11). */
  var wakeEvents = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
  var idleHandle = null;
  var fallbackTimer = null;
  var pageLoaded = document.readyState === 'complete';
  window.addEventListener('load', function () { pageLoaded = true; }, { once: true });

  function disarmKlaviyo() {
    for (var i = 0; i < wakeEvents.length; i++) {
      window.removeEventListener(wakeEvents[i], loadKlaviyo, { capture: true });
    }
    document.removeEventListener('focusin', wakeOnEmailField, true);
    window.removeEventListener('load', armKlaviyoFallbackTimer);
    if (idleHandle && window.cancelIdleCallback) cancelIdleCallback(idleHandle);
    if (fallbackTimer) clearTimeout(fallbackTimer);
    idleHandle = null;
    fallbackTimer = null;
  }

  function loadKlaviyo() {
    if (klaviyoRequested) return;
    if (internalVisit()) { disarmKlaviyo(); return; }
    klaviyoRequested = true;
    disarmKlaviyo();
    try {
      var tag = document.createElement('script');
      tag.async = true;
      tag.src = 'https://static.klaviyo.com/onsite/js/' + KLAVIYO_COMPANY_ID
        + '/klaviyo.js?company_id=' + KLAVIYO_COMPANY_ID;
      (document.head || document.documentElement).appendChild(tag);
    } catch (e) {}
  }

/*
  Focus on an email field is the last quiet moment before a submit. Load there so
  the SDK is warm by the time identify() runs, whichever surface the field
  belongs to: the welcome popup, notify-me, footer or contact.
*/
  function wakeOnEmailField(event) {
    var el = event && event.target;
    try {
      if (el && el.tagName === 'INPUT' && String(el.type).toLowerCase() === 'email') loadKlaviyo();
    } catch (e) {}
  }

/*
  Safari has never shipped requestIdleCallback, so iOS takes the timer, and it
  starts after the load event rather than on a fixed countdown from parse, so it
  is out of the critical path by construction.
*/
  function armKlaviyoFallbackTimer() {
    if (klaviyoRequested || fallbackTimer) return;
    fallbackTimer = setTimeout(loadKlaviyo, 1500);
  }

  function armKlaviyo() {
    for (var i = 0; i < wakeEvents.length; i++) {
      window.addEventListener(wakeEvents[i], loadKlaviyo, { passive: true, capture: true });
    }
    document.addEventListener('focusin', wakeOnEmailField, true);
    /* Idle only after `load`: requestIdleCallback also fires during the
       network wait before load (measured 2026-09-11: klaviyo.js at 1.6 s with
       load at 3.5 s on a fresh profile), the window this gate protects. */
    var armIdle = function () {
      if (klaviyoRequested || idleHandle || fallbackTimer) return;
      /* Five quiet seconds after load before the idle path, not the first idle
         frame (which lands a few ms after load, still inside the window a slow
         phone is painting and Lighthouse is tracing). A tap still loads at once. */
      fallbackTimer = setTimeout(function () {
        fallbackTimer = null;
        if (klaviyoRequested) return;
        if (window.requestIdleCallback) idleHandle = requestIdleCallback(loadKlaviyo);
        else armKlaviyoFallbackTimer();
      }, typeof window.__ssIdleAfterLoadMs === 'number' ? window.__ssIdleAfterLoadMs : 5000);
    };
    if (pageLoaded) armIdle();
    else window.addEventListener('load', armIdle, { once: true });
  }

  /*
The inline stub in snippets/klaviyo-onsite.liquid defines a queueing SSKlv
during parse so a body script that fires before this deferred asset runs is
never dropped. Take what it collected, then replace it.
  */
  var early = (window.SSKlv && window.SSKlv.q) || [];

  window.SSKlv = {
    identify: function (email) {
      if (!email || typeof email !== 'string') return;
      /*
 An email is the whole point of this file, and a shopper
      who hands one over is usually about to leave the page. Fetch the SDK now
      rather than wait for an idle frame that may never come before unload.
      
*/
      loadKlaviyo();
      /* A new name may own a basket the last one did not: tell it again. */
      lastBasketId = null;
      submit(['identify', { email: email.trim().toLowerCase() }]);
      /* Every email capture on the site (popup, cart drawer, the delegated form
         net below, a logged-in customer) passes through here, so this is where
         the rest of tracking hears about it: the GTM sign-up capture listens for
         ss:identified and hands the browser's identity to the server's email
         bridge (tracking wave 9c), which gives the Meta Lead its fbp and IP. */
      /* __ssLastIdentified covers an identify that runs before GTM is listening
         (a logged-in customer is identified at boot). */
      try {
        window.__ssLastIdentified = email.trim().toLowerCase();
        document.dispatchEvent(new CustomEvent('ss:identified', { detail: { email: email.trim().toLowerCase() } }));
      } catch (eventError) {}
    },
    track: function (name, props) {
      if (!name) return;
      submit(['track', name, props || {}]);
    },
    basket: function (cart, source) {
      var props = basketProps(cart, source);
      if (!props || props.$event_id === lastBasketId) return;
      lastBasketId = props.$event_id;
      window.SSKlv.track('Added to Cart', props);
    }
  };

/*
  The basket, for the abandoned-basket flow (AB r6, TwHe39, triggers on the
  "Added to Cart" API metric RGjUi3). Klaviyo audit 6 Oct 2026: 11 of 441 such
  events since 24 Sep sat on a profile with an email, because the only
  identified one came from the cart-drawer signup (sacrasoul-cart-email.js) and
  server tag 16 sends at the moment of the add, mostly for a browser it cannot
  name. Now every confirmed add sends the whole basket from here too.

  When it runs: the cart engine (assets/sacrasoul-cart-drawer.js,
  announceBasket) dispatches sacrasoul:basket-confirmed with Shopify's own cart
  once every add in flight has been read back, from any surface (PDP, sticky
  bar, quick add, the drawer's rail, the browse quiz, the Jade v2 buy box: they
  all land in its addLanded). The work waits for an idle frame and keeps only
  the newest cart, so a burst of taps is one event and nothing runs inside the
  tap or the drawer's paint. It never fetches klaviyo.js: by the time a shopper
  has tapped Add the first-input wake has already done that.

  Who receives it: klaviyo.js sends a track only for a browser it can name
  (logged in, a past signup, a click from one of our emails). For anyone else
  it keeps the event in its own localStorage cache (kl-post-identification-sync)
  and posts it if this browser is named later, so an anonymous shopper costs no
  request and creates no anonymous profile. Read from the live klaviyo.js,
  6 Oct 2026 (fender_analytics: track() returns early to cacheEvent when the
  identity is empty).

  One event per basket, not per tap: $event_id is klaviyo.js's unique_id (it
  maps $event_id -> unique_id and $value -> value), and Klaviyo records only
  the first event with a given unique_id per profile and metric. The id is the
  cart token (without its ?key= secret) plus the paid lines as variant x
  quantity (x selling plan), so the same basket told twice (a repeated add that
  ends where it was, an undo, the signup re-tell) collapses, and a different
  basket or a new cart after an order does not. The total is deliberately not
  in it: SACRA10 lands on the cart at the very moment the signup re-tells the
  basket, and the same three bottles must not become a second event.

  A gift line is not a basket: free (_gift or zero-priced) lines are left out
  of Items and of the id, and a cart holding only the gift sends nothing.
  Payload: the shape the flow's templates read (Items[] with ProductName,
  ProductID = the variant id, Quantity, ItemPrice, URL), plus Currency and
  $value, the same keys server tag 16 sends.
*/
  var lastBasketId = null;

  function basketHash(text) {
    var h = 0x811c9dc5;
    for (var i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h.toString(36);
  }

  function basketProps(cart, source) {
    try {
      if (!cart || !cart.items || !cart.items.length) return null;
      var token = String(cart.token || '').split('?')[0];
      if (!token) return null;
      var items = [];
      var lines = [];
      var count = 0;
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        if (!it || (it.properties && it.properties._gift === 'true')) continue;
        if (Number(it.final_line_price || 0) === 0) continue;
        var qty = Number(it.quantity || 1);
        var plan = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan
          ? it.selling_plan_allocation.selling_plan.id : '';
        var row = {
          ProductID: String(it.variant_id),
          ProductName: it.product_title || it.title,
          ItemPrice: Number(it.final_price || 0) / 100,
          Quantity: qty
        };
        if (it.sku) row.SKU = String(it.sku);
        if (it.vendor) row.Brand = String(it.vendor);
        if (it.product_type) row.Categories = [String(it.product_type)];
        if (it.image) row.ImageURL = String(it.image);
        if (it.url) row.URL = String(it.url);
        items.push(row);
        lines.push(it.variant_id + 'x' + qty + (plan ? 'p' + plan : ''));
        count += qty;
      }
      if (!items.length) return null;
      return {
        Items: items,
        Currency: cart.currency,
        $value: Number(cart.total_price || 0) / 100,
        page: window.location.href,
        Source: source || 'Sacrasoul Storefront',
        $event_id: 'basket:' + token + ':' + count + ':' + basketHash(lines.sort().join(','))
      };
    } catch (e) { return null; }
  }

  var basketCart = null;
  function sendBasket() {
    var cart = basketCart;
    basketCart = null;
    if (cart) window.SSKlv.basket(cart);
  }
  document.addEventListener('sacrasoul:basket-confirmed', function (event) {
    var cart = event && event.detail && event.detail.cart;
    if (!cart) return;
    var scheduled = !!basketCart;
    basketCart = cart;
    if (scheduled) return;
    if (window.requestIdleCallback) requestIdleCallback(sendBasket, { timeout: 3000 });
    else setTimeout(sendBasket, 250);
  });

  /*
 Prerender gate: identify, Viewed Product and the SDK load wait for
    prerenderingchange on a prerendered document (product links are prerendered);
    every other document runs this at once, as before. 
*/
  function bootWhenVisible() {
  /* Whatever the stub collected, in the order it arrived, before this page's
     own calls. Inside the prerender gate: a prerendered document must not
     fetch klaviyo.js for a page the shopper may never activate. */
  for (var ei = 0; ei < early.length; ei++) {
    var call = early[ei];
    if (call[0] === 'identify') window.SSKlv.identify(call[1]);
    else window.SSKlv.track(call[1], call[2]);
  }
  early.length = 0;

  var data = window.__ssKlv || {};
  if (data.email) window.SSKlv.identify(data.email);

  /*
Delegated identity net: any form submitted with a filled email field
(footer newsletter, signup banners, contact) identifies the browser, so
every present and future email capture feeds browse-abandonment without
per-form wiring. Capture phase so it runs even when a handler calls
stopPropagation; native (navigating) submits still usually flush because
klaviyo.js sends its events keepalive.
  */
  document.addEventListener('submit', function (e) {
    try {
      var form = e.target;
      if (!form || !form.querySelector) return;
      var field = form.querySelector('input[type="email"]');
      var v = field && field.value;
      if (v && v.indexOf('@') > 0) window.SSKlv.identify(v);
    } catch (err) {}
  }, true);

  /*
Currency and $value are not decoration: without them a PLN 190 view and a
GBP 190 view are the same number downstream, and the sGTM lane (tag 17)
already sends both. $value is Klaviyo's reserved key for the monetary
amount a flow or segment can compare on. The payload is rendered by
snippets/klaviyo-onsite.liquid, the only place Liquid can read the product.
  */
  if (data.product) window.SSKlv.track('Viewed Product', data.product);

  armKlaviyo();
  }
  if (document.prerendering) document.addEventListener('prerenderingchange', bootWhenVisible, { once: true });
  else bootWhenVisible();
})();
